/** @jsxImportSource @emotion/react */
import { Paper as MuiPaper, useMediaQuery } from '@material-ui/core';
import {
  breakpoints,
  Buttons,
  Selectable,
  TextFields,
} from '@zip/business-components';
import { BasePage } from 'components';
import { useFeatureFlagsReady } from 'contexts';
import { FeatureFlag, PageRoute, ProductClassification } from 'enums';
import { css } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import CustomiseOfferSidebar from 'modules/products-and-offers-customise-sidebar';
import HowItWorksDialogModule from 'modules/products-and-offers-how-it-works-dialog';
import ProductsAndOffersTable from 'modules/products-and-offers-table';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PageProps } from 'types';
import * as styles from './ProductsConfigPage.styles';

const ProductsConfigPage: FC<PageProps> = ({}) => {
  const productOptions: Selectable[] = [
    { label: 'All products', value: '' },
    { label: 'Zip Money', value: ProductClassification.ZipMoney.toString() },
    { label: 'Zip Plus', value: ProductClassification.ZipPlus.toString() },
    { label: 'Zip Pay', value: ProductClassification.ZipPay.toString() },
  ];

  const history = useHistory();
  const featureFlagReady = useFeatureFlagsReady();
  const productConfigEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableProductConfig
  );

  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const [currentProduct, setCurrentProduct] = useState<string>('');
  const [howItWorksLinkOpen, setHowItWorksLinkOpen] = useState<boolean>(false);
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

  const customiseOffersButton = (
    <Buttons.Primary
      onClick={(): void => {
        setSidebarOpen(!sidebarOpen);
      }}
      fullWidth={isMobile}
    >
      <span id="buttonLabel">Customise offers</span>
    </Buttons.Primary>
  );

  const HowItWorksDialogLink = (
    <Buttons.Text onClick={(): void => setHowItWorksLinkOpen(true)}>
      How our Zip products work
    </Buttons.Text>
  );

  const HowItWorksDialogButton = (
    <Buttons.Tertiary
      onClick={(): void => setHowItWorksLinkOpen(true)}
      fullWidth={isMobile}
      css={isMobile ? css.marginTop(12) : ''}
    >
      How our Zip products work
    </Buttons.Tertiary>
  );

  useEffect(() => {
    if (featureFlagReady.merchantId && !productConfigEnabled) {
      history.push(PageRoute.Home);
    }
  }, [featureFlagReady.merchantId, productConfigEnabled]);

  return (
    <BasePage
      title="Products & Offers"
      variant="layout"
      fullWidth
      flex
      action={!isMobile && HowItWorksDialogLink}
    >
      <MuiPaper
        elevation={0}
        css={styles.productsAndOffersTableContainer(sidebarOpen)}
      >
        <div id="toolbar" css={styles.toolbar}>
          <span>
            <TextFields.Select
              id="product-dropdown"
              label="Filter by product"
              options={productOptions}
              css={styles.select}
              onChange={(e): void => setCurrentProduct(e.target.value)}
              value={currentProduct}
              variant="outlined"
              fullWidth={isMobile}
            />
            {isMobile && customiseOffersButton}
            {isMobile && HowItWorksDialogButton}
          </span>
          {!isMobile && customiseOffersButton}
        </div>
        {
          <ProductsAndOffersTable
            selectedProduct={
              currentProduct === ''
                ? undefined
                : (parseInt(currentProduct) as ProductClassification)
            }
          />
        }
      </MuiPaper>

      <CustomiseOfferSidebar
        open={sidebarOpen}
        onClose={(): void => setSidebarOpen(false)}
      />

      <HowItWorksDialogModule
        open={howItWorksLinkOpen}
        onClose={(): void => setHowItWorksLinkOpen(false)}
      />
    </BasePage>
  );
};

export default ProductsConfigPage;
